import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, TruckIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiError } from "@/lib/api";
import { money, fmtDate } from "@/lib/constants";
import { useLang } from "@/i18n";
import { Panel, Empty, RowSearch } from "@/pages/shared";
import { SupplierFields, blankSupplier } from "@/pages/admin/AdminSuppliers";
import { uid } from "@/lib/uid";

const blank = () => ({ supplier: "", supplier_id: "", invoice_number: "", date: new Date().toISOString().slice(0, 10), note: "", lines: [] });

export default function AdminPurchases() {
  const { t } = useLang();
  const [purchases, setPurchases] = useState([]);
  const [articles, setArticles] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [newSup, setNewSup] = useState(null);
  const [purchase, setPurchase] = useState(blank());
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [p, a, s] = await Promise.all([api.get("/purchases"), api.get("/articles"), api.get("/suppliers")]);
      setPurchases(p.data); setArticles(a.data); setSuppliers(s.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    return purchases.filter((p) => !s || [p.supplier, p.invoice_number].some((v) => (v || "").toLowerCase().includes(s)));
  }, [purchases, q]);

  const total = purchase.lines.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.unit_price) || 0), 0);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/purchases", { ...purchase, lines: purchase.lines.map((l) => ({ article_id: l.article_id, qty: Number(l.qty), unit_price: Number(l.unit_price) || 0 })) });
      setOpen(false); setPurchase(blank()); await load(); toast.success(t("delivery_booked"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const setLine = (i, key, val) => {
    const lines = purchase.lines.map((x, j) => j === i ? { ...x, [key]: val } : x);
    setPurchase({ ...purchase, lines });
    if (purchase.supplier_id && (key === "article_id" || key === "qty")) fillPrice(i, lines[i]);
  };

  // Afgesproken staffelprijs van de leverancier overnemen
  const fillPrice = async (i, line) => {
    if (!line.article_id) return;
    try {
      const { data } = await api.get(`/suppliers/${purchase.supplier_id}/price?article_id=${line.article_id}&qty=${Number(line.qty) || 1}`);
      if (data.price != null) setPurchase((p) => ({ ...p, lines: p.lines.map((x, j) => j === i ? { ...x, unit_price: data.price } : x) }));
    } catch (e) { console.warn("Geen prijsafspraak voor dit artikel", e?.message); }
  };

  const pickSupplier = (id) => {
    const s = suppliers.find((x) => x.id === id);
    setPurchase((p) => ({ ...p, supplier_id: id, supplier: s?.name || "" }));
    purchase.lines.forEach((l, i) => l.article_id && setTimeout(() => fillPrice(i, l), 0));
  };

  const saveSupplier = async () => {
    try {
      const { data } = await api.post("/suppliers", newSup);
      setSuppliers((list) => [...list, data].sort((a, b) => a.name.localeCompare(b.name)));
      setPurchase((p) => ({ ...p, supplier_id: data.id, supplier: data.name }));
      setNewSup(null); toast.success(t("saved"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Panel
      title={`${t("admin_purchases")} (${visible.length}${q ? `/${purchases.length}` : ""})`}
      testid="purchases-panel"
      action={
        <div className="flex items-center gap-2">
          <RowSearch value={q} onChange={setQ} placeholder={t("supplier")} testid="purchases-search" />
          <Button onClick={() => { setPurchase(blank()); setOpen(true); }} data-testid="new-purchase-button"><TruckIcon className="h-4 w-4 mr-1" />{t("new_delivery")}</Button>
        </div>
      }
    >
      <div className="px-5 pt-4 text-sm text-muted-foreground">{t("admin_purchases_desc")}</div>
      {visible.length === 0 ? <Empty /> : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("date")}</TableHead>
              <TableHead>{t("supplier")}</TableHead>
              <TableHead>{t("invoice_number")}</TableHead>
              <TableHead>{t("art_lines")}</TableHead>
              <TableHead className="text-right">{t("total")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((p) => (
              <TableRow key={p.id} data-testid={`purchase-row-${p.id}`}>
                <TableCell className="text-muted-foreground">{fmtDate(p.date)}</TableCell>
                <TableCell className="font-semibold text-foreground">{p.supplier}</TableCell>
                <TableCell className="font-mono text-xs">{p.invoice_number}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{p.lines.map((l) => `${l.qty}× ${l.name}`).join(", ")}</TableCell>
                <TableCell className="text-right font-semibold tabular-nums">{money(p.total)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="max-w-2xl" data-testid="purchase-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><TruckIcon className="h-4 w-4" />{t("new_delivery")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">{t("supplier")}</Label>
              <div className="flex gap-1.5">
                <Select value={purchase.supplier_id} onValueChange={pickSupplier}>
                  <SelectTrigger data-testid="purchase-supplier"><SelectValue placeholder={t("select_supplier")} /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {suppliers.filter((s) => s.active !== false).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="icon" className="shrink-0" title={t("new_supplier")}
                  onClick={() => setNewSup(blankSupplier())} data-testid="purchase-new-supplier"><Plus className="h-4 w-4" /></Button>
              </div>
            </div>
            <div className="space-y-1"><Label className="text-xs">{t("invoice_number")}</Label><Input value={purchase.invoice_number} onChange={(e) => setPurchase({ ...purchase, invoice_number: e.target.value })} data-testid="purchase-number" /></div>
            <div className="space-y-1"><Label className="text-xs">{t("date")}</Label><Input type="date" value={purchase.date} onChange={(e) => setPurchase({ ...purchase, date: e.target.value })} data-testid="purchase-date" /></div>
          </div>
          <div className="space-y-2">
            {purchase.lines.map((l, i) => (
              <div key={l._uid} className="grid grid-cols-[1fr_5rem_6rem_6rem_2rem] gap-2 items-end" data-testid={`purchase-line-${i}`}>
                <div className="space-y-1">
                  <Label className="text-xs">{t("art_name")}</Label>
                  <Select value={l.article_id} onValueChange={(v) => setLine(i, "article_id", v)}>
                    <SelectTrigger data-testid={`purchase-article-${i}`}><SelectValue placeholder={t("select_article")} /></SelectTrigger>
                    <SelectContent className="max-h-64">{articles.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} · {a.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label className="text-xs">{t("qty")}</Label><Input type="number" step="1" value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} data-testid={`purchase-qty-${i}`} /></div>
                <div className="space-y-1"><Label className="text-xs">{t("purchase_price")}</Label><Input type="number" step="0.01" value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} data-testid={`purchase-price-${i}`} /></div>
                <span className="pb-2 text-sm font-semibold tabular-nums text-right" data-testid={`purchase-line-total-${i}`}>{money((Number(l.qty) || 0) * (Number(l.unit_price) || 0))}</span>
                <Button variant="ghost" size="sm" onClick={() => setPurchase({ ...purchase, lines: purchase.lines.filter((_, j) => j !== i) })}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
              </div>
            ))}
            <div className="flex items-center justify-between">
              <Button variant="outline" size="sm" onClick={() => setPurchase({ ...purchase, lines: [...purchase.lines, { _uid: uid(), article_id: "", qty: 1, unit_price: "" }] })} data-testid="purchase-add-line"><Plus className="h-4 w-4 mr-1" />{t("add_line")}</Button>
              <span className="text-sm text-muted-foreground">{t("total")}: <span className="font-display font-extrabold text-primary tabular-nums" data-testid="purchase-total">{money(total)}</span></span>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">{t("delivery_hint")}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={save} disabled={busy || !purchase.supplier || !purchase.invoice_number || purchase.lines.length === 0} data-testid="save-purchase-button">{t("book_delivery")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Leverancier aanmaken tijdens het boeken */}
      <Dialog open={!!newSup} onOpenChange={(o) => !o && setNewSup(null)}>
        <DialogContent className="max-w-2xl" data-testid="purchase-supplier-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("new_supplier")}</DialogTitle></DialogHeader>
          {newSup && <SupplierFields form={newSup} setForm={setNewSup} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewSup(null)}>{t("cancel")}</Button>
            <Button onClick={saveSupplier} disabled={!newSup?.name?.trim()} data-testid="save-new-supplier">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Panel>
  );
}
